import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateResellerOrder } from '@/hooks/useResellerOrders';

export default function ResellerNewOrderPage() {
  const nav = useNavigate();
  const create = useCreateResellerOrder();
  const [form, setForm] = useState({
    customer_email: '', customer_name: '', business_name: '', contact_phone: '',
    domain: '', plan_id: '', amount: '', currency: 'USD', notes: '',
  });

  const plans = useQuery({
    queryKey: ['plans-active'],
    queryFn: async () => {
      const { data, error } = await supabase.from('plans').select('id,name,price,currency').eq('is_active', true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const set = <K extends keyof typeof form>(k: K, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await create.mutateAsync({
      customer_email: form.customer_email,
      customer_name: form.customer_name || undefined,
      business_name: form.business_name || undefined,
      contact_phone: form.contact_phone || undefined,
      domain: form.domain || undefined,
      plan_id: form.plan_id || undefined,
      amount: Number(form.amount || 0),
      currency: form.currency,
      notes: form.notes || undefined,
    });
    nav('/reseller/orders');
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader><CardTitle>Enroll a customer</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Customer email *" required>
              <Input type="email" value={form.customer_email} onChange={(e) => set('customer_email', e.target.value)} required />
            </Field>
            <Field label="Customer name">
              <Input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} />
            </Field>
            <Field label="Business name">
              <Input value={form.business_name} onChange={(e) => set('business_name', e.target.value)} />
            </Field>
            <Field label="Phone">
              <Input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />
            </Field>
            <Field label="Domain">
              <Input placeholder="acme.referme.site" value={form.domain} onChange={(e) => set('domain', e.target.value)} />
            </Field>
            <Field label="Plan">
              <Select value={form.plan_id} onValueChange={(v) => set('plan_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select plan (optional)" /></SelectTrigger>
                <SelectContent>
                  {(plans.data ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {p.price} {p.currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Amount *" required>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set('amount', e.target.value)} required />
            </Field>
            <Field label="Currency">
              <Input value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Notes">
                <Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
              </Field>
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => nav('/reseller/orders')}>Cancel</Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Submitting…' : 'Create order'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}{required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}
